import { useCallback, useEffect, useState } from "react";
import api, { getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarRange } from "lucide-react";

const TYPES = [
  ["FULL_YEAR", "Tahun Penuh"],
  ["INTERIM", "Interim"],
  ["PARTIAL_PERIOD", "Periode Sebagian"],
  ["QUARTER", "Kuartal"],
  ["MONTH", "Bulan"],
  ["CUSTOM_DATE_RANGE", "Kustom"],
];
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const needsRange = (kind) => ["INTERIM", "PARTIAL_PERIOD", "CUSTOM_DATE_RANGE"].includes(kind);

// The auditor states the audit period; it is never inferred from a file name or from row dates.
export default function AuditPeriod({ id, onChange }) {
  const [period, setPeriod] = useState(null);
  const [display, setDisplay] = useState("");
  const [kind, setKind] = useState("FULL_YEAR");
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState("Q1");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/engagements/${id}/audit-period`).then(({ data }) => {
      setPeriod(data.audit_period); setDisplay(data.display);
      if (data.audit_period) {
        setKind(data.audit_period.audit_period_type);
        setYear(data.audit_period.fiscal_year);
        setQuarter(data.audit_period.quarter || "Q1");
        setStart(data.audit_period.period_start || "");
        setEnd(data.audit_period.period_end || "");
      }
    }).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = { audit_period_type: kind, fiscal_year: Number(year) };
    if (kind === "QUARTER") body.quarter = quarter;
    if (kind === "MONTH") body.period_start = start;
    if (needsRange(kind)) { body.period_start = start; body.period_end = end; }
    setBusy(true);
    try {
      const { data } = await api.put(`/engagements/${id}/audit-period`, body);
      setPeriod(data.audit_period); setDisplay(data.display);
      toast.success("Periode audit dicatat");
      if (onChange) onChange(data.audit_period);
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-sm p-4 space-y-3" data-testid="audit-period">
      <div className="flex items-center gap-2 font-head font-semibold text-sm">
        <CalendarRange size={15} />Periode Audit
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Tipe</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="h-9 w-48" data-testid="ap-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Tahun Buku</Label>
          <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="h-9 w-28" data-testid="ap-year" /></div>
        {kind === "QUARTER" && (
          <div><Label className="text-xs">Kuartal</Label>
            <Select value={quarter} onValueChange={setQuarter}>
              <SelectTrigger className="h-9 w-28" data-testid="ap-quarter"><SelectValue /></SelectTrigger>
              <SelectContent>{QUARTERS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {(kind === "MONTH" || needsRange(kind)) && (
          <div><Label className="text-xs">Dari</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-40" data-testid="ap-start" /></div>
        )}
        {needsRange(kind) && (
          <div><Label className="text-xs">Sampai</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-40" data-testid="ap-end" /></div>
        )}
        <Button onClick={save} disabled={busy} data-testid="ap-save">Simpan Periode</Button>
      </div>
      <div className="text-xs text-muted-foreground" data-testid="ap-display">
        {period ? display : "Belum ada periode audit yang dicatat — tetapkan sebelum menyusun populasi GL."}
      </div>
    </div>
  );
}
